import {languages} from '@codemirror/language-data'
import type {EditorView} from '@codemirror/view'
import {loadLanguageTheme} from '../themes/language-themes'
import {StateEffect, type Compartment, type Extension, type TransactionSpec} from '@codemirror/state'
import {getTheme} from '../themes/github-theme'
import {markdown, markdownLanguage} from '@codemirror/lang-markdown'
import {emojiAutocomplete} from '../autocomplete/emoji-autocomplete'
import {markdownExtension} from '../extensions/markdown'

export async function loadLanguage({
  autoCompleteCompartment,
  view,
  viewB,
  fileName,
  languageCompartment,
  managedExtensions,
  themeCompartment,
}: {
  autoCompleteCompartment: Compartment
  /** the primary EditorView */
  view?: EditorView
  /** the second EditorView (e.g. the right side of a split diff) */
  viewB?: EditorView
  fileName: string
  languageCompartment: Compartment
  managedExtensions: Extension[]
  themeCompartment: Compartment
}) {
  const language = resolveLanguage(fileName)

  if (language && view) {
    const languageTheme = loadLanguageTheme(language.name)

    if (languageTheme) {
      dispatch(view, viewB, {
        effects: themeCompartment.reconfigure(getTheme(languageTheme)),
      })
    }

    if (language.name === 'Markdown') {
      // use gfm as the base for markdown language
      dispatch(view, viewB, {
        effects: languageCompartment.reconfigure(markdown({base: markdownLanguage})),
      })

      // add markdown extensions
      dispatch(view, viewB, {
        effects: StateEffect.reconfigure.of([...markdownExtension, ...managedExtensions]),
      })

      // add emoji autocomplete - this needs its own compartment
      dispatch(view, viewB, {effects: autoCompleteCompartment.reconfigure(emojiAutocomplete())})
    } else {
      dispatch(view, viewB, {effects: autoCompleteCompartment.reconfigure([])}) // reset autocomplete
      dispatch(view, viewB, {effects: languageCompartment.reconfigure(await language.load())})
    }
  }
}

export function resolveLanguage(fileName: string) {
  if (
    fileName === 'actions.lock' ||
    fileName === '.github/workflows/actions.lock' ||
    fileName === '.github/workflows-lab/actions.lock'
  ) {
    return languages.find(language => language.name === 'YAML')
  }

  const fileExtension = fileName.split('.').pop() || ''
  if (!fileExtension) {
    return undefined
  }

  return languages.find(
    language => language.extensions.includes(fileExtension) || (language.filename && language.filename.test(fileName)),
  )
}

function dispatch(view: EditorView, viewB: EditorView | undefined, transaction: TransactionSpec) {
  view.dispatch(transaction)
  if (viewB) {
    viewB.dispatch(transaction)
  }
}
